import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Logo3D from "@/components/Logo3D";
import StatsBand from "@/components/StatsBand";
import BreakthroughSection from "@/components/BreakthroughSection";
import ServicesAccordion from "@/components/ServicesAccordion";
import { useTheme } from "@/lib/theme";
import { api } from "@/lib/api";
import { FAQS, TESTIMONIALS } from "@/lib/config";
import {
  ArrowRight,
  Check,
  Clock,
  Rocket,
  Layers,
  Film,
  Image as ImageIcon,
  GalleryHorizontal,
  Compass,
  Podcast,
  Gamepad2,
  BookOpen,
  Gem,
  Minus,
  Zap,
  UserRound,
  Wand2,
  Upload,
  Pencil,
  Package,
  CreditCard,
  Search,
  Truck,
  DownloadCloud,
  Quote,
  Clapperboard,
  Scissors,
} from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const SERVICE_ICON_MAP = {
  premium_short: Clapperboard,
  volume_short: Scissors,
  long_form: Film,
  thumbnails: ImageIcon,
  carousels: GalleryHorizontal,
  strategy: Compass,
};

const STYLE_ICON = {
  hormozi: Zap,
  gadzhi: Gem,
  documentary: Film,
  podcast: Podcast,
  gaming: Gamepad2,
  educational: BookOpen,
  luxury: Rocket,
  minimal: Minus,
  high_energy: Zap,
  custom: Wand2,
};

const HERO_IMG =
  "https://images.unsplash.com/photo-1687575635557-a3f3ed535b56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB2aWRlbyUyMGVkaXRvciUyMGJsYWNrJTIwYW5kJTIwd2hpdGV8ZW58MHx8fHwxNzg0NzcxMDMzfDA&ixlib=rb-4.1.0&q=85";

const HOW_STEPS = [
  {
    // Each step has separate dark/light artwork. The card renders the
    // variant that matches the currently active theme.
    imageDark: "/how/step-1-dark.webp",
    imageLight: "/how/step-1-light.webp",
    imageAlt: "Build your order — create your own content package (short-form, long-form, clipping).",
    Icon: Package,
  },
  {
    imageDark: "/how/step-2-dark.webp",
    imageLight: "/how/step-2-light.webp",
    imageAlt: "Choose a style — pick from Hormozi, Gadzhi, Documentary, Podcast, or custom direction.",
    Icon: Pencil,
  },
  {
    imageDark: "/how/step-3-dark.webp",
    imageLight: "/how/step-3-light.webp",
    imageAlt: "Upload — share links from YouTube, Drive, Dropbox, Vimeo, Loom or any URL.",
    Icon: Upload,
  },
  {
    imageDark: "/how/step-4-dark.webp",
    imageLight: "/how/step-4-light.webp",
    imageAlt: "Checkout — secure checkout with card, Apple Pay, Google Pay or crypto.",
    Icon: CreditCard,
  },
  {
    imageDark: "/how/step-5-dark.webp",
    imageLight: "/how/step-5-light.webp",
    imageAlt: "Track order — real-time detailed order tracking with progress and deliverables.",
    Icon: Truck,
  },
  {
    imageDark: "/how/step-6-dark.webp",
    imageLight: "/how/step-6-light.webp",
    imageAlt: "Receive order — download viral-ready content, ready to publish.",
    Icon: DownloadCloud,
  },
];

/* --------------------------- Segmented Toggle --------------------------- */
function SegmentedToggle({ options, value, onChange, testIdPrefix }) {
  const ref = useRef(null);
  const [style, setStyle] = useState({ left: 4, width: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const active = el.querySelector(`button[data-active="true"]`);
    if (active) {
      const parentRect = el.getBoundingClientRect();
      const rect = active.getBoundingClientRect();
      setStyle({ left: rect.left - parentRect.left, width: rect.width });
    }
  }, [value, options]);

  return (
    <div className="seg-toggle" ref={ref} data-testid={`${testIdPrefix}-toggle`}>
      <span
        className="seg-thumb"
        style={{ left: `${style.left}px`, width: `${style.width}px` }}
        aria-hidden
      />
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          data-active={value === o.value}
          data-testid={`${testIdPrefix}-${o.value}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* --------------------------------- Page --------------------------------- */
export default function Landing() {
  const [pkg, setPkg] = useState(null);
  const [billingMode, setBillingMode] = useState("one_time"); // "one_time" | "subscription"
  const [cadence, setCadence] = useState("quarterly"); // when subscription: monthly | quarterly | yearly
  const collageTrackRef = useRef(null);
  const { theme } = useTheme();

  useEffect(() => {
    api.get("/packages").then((r) => setPkg(r.data)).catch(() => {});
  }, []);

  // Capture affiliate referral code from ?ref=CODE query param.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("ref");
      if (code) {
        localStorage.setItem("bliencer_ref", code);
        api.post(`/affiliate/track/${encodeURIComponent(code)}`).catch(() => {});
      }
    } catch {
      /* localStorage may be unavailable — degrade silently */
    }
  }, []);

  /**
   * Cinematic collage scroll — RAF-driven.
   *
   * We deliberately do NOT use a CSS @keyframes animation here: iOS Safari
   * silently freezes percentage transforms on layers already promoted via
   * ancestor perspective/translateZ+will-change (compositor memory
   * eviction with large images). A JS RAF driver that writes a
   * pixel-based translate3d works on every browser + engine.
   */
  useEffect(() => {
    const track = collageTrackRef.current;
    if (!track) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const SPEED_PX_PER_SEC = 22;
    let raf;
    let last = performance.now();
    let offset = 0;
    let running = true;
    // Cache the image height OUTSIDE the RAF loop. Previously we called
    // getBoundingClientRect() every frame (~60/s) which triggered a forced
    // layout recalc → the #1 cause of scroll jank on this page.
    let cachedH = 0;

    const measure = () => {
      const imgs = track.querySelectorAll("picture img, img");
      for (const el of imgs) {
        if (el.offsetParent !== null) {
          const h = el.getBoundingClientRect().height;
          if (h > 0) { cachedH = h; return; }
        }
      }
      // Visible image not painted yet — fall back to naturalHeight math.
      const trackW = track.clientWidth;
      for (const el of imgs) {
        if (el.naturalWidth && el.naturalHeight && trackW) {
          cachedH = el.naturalHeight * (trackW / el.naturalWidth);
          return;
        }
      }
    };
    measure();
    // Remeasure on layout-affecting events only — never inside the RAF tick.
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    // Also remeasure when the light-mode images finish loading (in case
    // they weren't ready when measure() first ran on mount).
    track.querySelectorAll("img").forEach((img) => {
      if (!img.complete) img.addEventListener("load", measure, { once: true });
    });

    const tick = (now) => {
      if (!running) return;
      const dt = Math.min(now - last, 64);
      last = now;
      if (cachedH > 0) {
        offset = (offset + (dt / 1000) * SPEED_PX_PER_SEC) % cachedH;
        track.style.transform = `translate3d(0, ${-offset}px, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onVis = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else {
        running = true;
        last = performance.now();
        // Cheap: only measure if we lost the cache while hidden.
        if (cachedH === 0) measure();
        raf = requestAnimationFrame(tick);
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const subDiscount = useMemo(() => {
    if (billingMode !== "subscription" || !pkg) return 0;
    const plan = pkg.subscriptions?.find((p) => p.id === cadence);
    return plan?.discount || 0;
  }, [billingMode, cadence, pkg]);

  return (
    <div className="min-h-screen bg-black text-white">
      <Nav transparent />

      {/* ------------------------------ Hero ------------------------------ */}
      <section className="relative pt-40 pb-16 sm:pt-48 sm:pb-20 px-6 sm:px-10 overflow-hidden">
        {/* Cinematic 3D scrolling collage — sits behind hero content.
            One <picture> per theme; each picks a mobile-optimized source on
            small viewports via <source media>. Two stacked copies inside
            the track give a seamless infinite loop. */}
        <div className="hero-collage" aria-hidden>
          <div className="hero-collage__stage">
            <div className="hero-collage__track" ref={collageTrackRef}>
              {/* Dark theme copies */}
              <picture className="hero-collage__img--dark">
                <source media="(max-width: 767px)" srcSet="/hero/collage-sm.jpg" />
                <img src="/hero/collage.jpg" alt="" loading="eager" fetchpriority="high" />
              </picture>
              <picture className="hero-collage__img--dark">
                <source media="(max-width: 767px)" srcSet="/hero/collage-sm.jpg" />
                <img src="/hero/collage.jpg" alt="" loading="eager" fetchpriority="high" />
              </picture>
              {/* Light theme copies — eager so they're ready the instant the
                  user toggles theme (was lazy → caused visible animation
                  freeze while the light JPG downloaded on first flip).
                  fetchpriority="low" keeps them from competing with the
                  hero's initial paint. */}
              <picture className="hero-collage__img--light">
                <source media="(max-width: 767px)" srcSet="/hero/collage-sm-light.jpg" />
                <img src="/hero/collage-light.jpg" alt="" loading="eager" fetchpriority="low" />
              </picture>
              <picture className="hero-collage__img--light">
                <source media="(max-width: 767px)" srcSet="/hero/collage-sm-light.jpg" />
                <img src="/hero/collage-light.jpg" alt="" loading="eager" fetchpriority="low" />
              </picture>
            </div>
          </div>
          <div className="hero-collage__wash" />
          <div className="hero-collage__veil" />
        </div>

        <div className="relative max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="inline-flex items-center gap-2 squircle-sm border border-[#2b2b2b] bg-[#0d0d0d] px-3 py-1.5 text-xs text-[#a0a0a0] mb-8"
            data-testid="hero-badge"
          >
            <UserRound size={12} /> We believe in the value of human creativity.
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.05 }}
            className="text-5xl sm:text-6xl md:text-7xl lg:text-[80px] font-bold tracking-tighter leading-[0.98] max-w-5xl"
            data-testid="hero-headline"
          >
            <span className="hero-headline">The Difference Between</span>
            <br />
            <span className="hero-headline">Being Seen</span>{" "}
            <span className="hero-headline hero-headline--muted">and Being Scrolled Past.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.15 }}
            className="mt-8 text-lg sm:text-xl text-[#d4d4d4] max-w-2xl leading-relaxed hidden sm:block"
          >
            You’re a creator struggling to get views. We’ve helped creators go
            from 200–300 views to 1,000,000+ views within 8 weeks.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.25 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/order"
              data-testid="hero-cta-primary"
              className="btn-accent squircle px-6 py-3.5 font-medium btn-anim inline-flex items-center gap-2"
            >
              Start your order <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Stat band — animated count-up on scroll */}
      <StatsBand />

      {/* ------------------- Breakthrough (views spike) ------------------ */}
      <BreakthroughSection />

      {/* -------------------------- Services -------------------------- */}
      <section id="services" className="lazy-section pt-16 pb-[120px] px-6 sm:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-16">
            <div className="text-xs uppercase tracking-widest text-[#a0a0a0] mb-4">Services</div>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight">
              A full-stack creative team,<br />on-demand.
            </h2>
            <p className="mt-6 text-[#a0a0a0] leading-relaxed text-lg max-w-2xl">
              Short-form, long-form, thumbnails, carousels, and strategy —
              built by human editors and delivered on a schedule you set.
            </p>
          </div>

          <ServicesAccordion services={pkg?.services || []} />

          <div className="mt-16 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between squircle border border-[#2b2b2b] bg-[#0d0d0d] p-8">
            <div className="max-w-lg">
              <div className="text-xs uppercase tracking-widest text-[#a0a0a0] mb-2">
                Custom project
              </div>
              <div className="text-lg font-semibold tracking-tight">
                Need something specific? Let's map it out.
              </div>
              <div className="text-sm text-[#a0a0a0] mt-1">
                Mix any services in one order — pricing is calculated based on your exact scope, only revealed after you configure everything.
              </div>
            </div>
            <Link
              to="/order"
              data-testid="services-cta-build"
              className="btn-accent squircle-sm px-5 h-11 text-sm font-medium btn-anim inline-flex items-center gap-2 shrink-0"
            >
              Build your order <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ------------------------- How It Works ------------------------- */}
      <section id="how" className="lazy-section pt-16 pb-[120px] px-6 sm:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-xs uppercase tracking-widest text-[#a0a0a0] mb-4">
              The process
            </div>
            <h2
              className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter"
              data-testid="how-title"
            >
              How It Works
            </h2>
            <p className="mt-6 text-[#a0a0a0] leading-relaxed">
              From your raw footage to viral-ready content in six calm steps.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {HOW_STEPS.map((step, i) => {
              const Icon = step.Icon;
              // Pick the theme-matching artwork. Both variants are the
              // same 16:9 shape so the layout doesn't reflow on toggle.
              const image = theme === "light" ? step.imageLight : step.imageDark;
              // Image-only card: full-bleed art at the image's native
              // 16:9 aspect ratio, no card padding or number/icon
              // overlays. `aspect-video` + `object-contain` guarantees the
              // corners of the artwork are NEVER cropped at any viewport
              // width. `self-start` prevents the CSS grid from stretching
              // the card to match the height of taller text-based siblings.
              if (image) {
                return (
                  <motion.div
                    key={step.imageDark}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    className="relative squircle-lg border border-[#2b2b2b] overflow-hidden hover:-translate-y-1 btn-anim aspect-video self-start"
                    data-testid={`how-step-${i + 1}`}
                  >
                    <img
                      src={image}
                      alt={step.imageAlt || ""}
                      loading="lazy"
                      className="block w-full h-full object-contain"
                    />
                  </motion.div>
                );
              }
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="relative squircle-lg border border-[#2b2b2b] bg-[#111] p-8 pt-10 overflow-hidden hover:-translate-y-1 btn-anim"
                  data-testid={`how-step-${i + 1}`}
                >
                  <div
                    className="step-number absolute top-4 left-6 text-6xl sm:text-7xl"
                    aria-hidden
                  >
                    {i + 1}
                  </div>
                  <div className="relative flex items-start justify-end mb-8">
                    <div className="h-12 w-12 squircle-sm border border-[#2b2b2b] bg-[#0d0d0d] flex items-center justify-center">
                      <Icon size={20} />
                    </div>
                  </div>
                  <div className="relative">
                    <h3 className="text-xl font-semibold tracking-tight">{step.title}</h3>
                    <p className="mt-3 text-sm text-[#a0a0a0] leading-relaxed">{step.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="lazy-section py-[120px] px-6 sm:px-10 bg-[#070707] border-y border-[#181818]">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div
            className="squircle-lg overflow-hidden border border-[#2b2b2b] order-2 md:order-1 relative h-[520px] flex items-center justify-center"
            style={{
              background:
                "radial-gradient(120% 90% at 30% 20%, #1a1a1a 0%, #0a0a0a 55%, #050505 100%)",
            }}
            data-testid="why-preview-placeholder"
            aria-label="Preview image placeholder"
          >
            <img
              src="/brand/bliencer-mark.png"
              alt=""
              aria-hidden
              className="h-24 w-24 opacity-25"
            />
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.35em] text-[#5a5a5a]">
              Preview coming soon
            </div>
          </div>
          <div className="order-1 md:order-2">
            <div className="text-xs uppercase tracking-widest text-[#a0a0a0] mb-4">
              Why creators pick us
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
              Real editors. Real taste.
              <br />
              Real retention.
            </h2>
            <ul className="mt-10 grid sm:grid-cols-2 gap-4 text-sm">
              {[
                "100% human editors",
                "Human-directed storytelling",
                "Professional narrative pacing",
                "Retention-focused editing",
                "Platform optimization",
                "Fast delivery",
                "Premium QC pipeline",
                "NDA + secure storage",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-[#e5e5e5]">
                  <Check size={16} className="mt-0.5 text-white" /> {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>



      {/* ------------------------- Testimonials ------------------------- */}
      <section id="testimonials" className="lazy-section py-[120px] px-6 sm:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-xs uppercase tracking-widest text-[#a0a0a0] mb-4">
              Testimonials
            </div>
            <h2
              className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter"
              data-testid="testimonials-title"
            >
              Testimonials
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="testimonial-card squircle-lg p-8 flex flex-col justify-between min-h-[280px]"
                data-testid={`testimonial-${i}`}
              >
                <div>
                  <Quote
                    size={36}
                    strokeWidth={1.75}
                    className="t-muted"
                    aria-hidden
                  />
                  <blockquote className="mt-5 t-white leading-relaxed">
                    {t.quote}
                  </blockquote>
                </div>
                <figcaption className="mt-8 flex items-center gap-3">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="h-10 w-10 squircle-sm object-cover border t-border grayscale"
                  />
                  <div>
                    <div className="text-sm font-medium t-white">{t.name}</div>
                    <div className="text-xs t-muted">{t.role}</div>
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="lazy-section py-[120px] px-6 sm:px-10 border-t border-[#181818] bg-[#070707]">
        <div className="max-w-4xl mx-auto">
          <div className="mb-16">
            <div className="text-xs uppercase tracking-widest text-[#a0a0a0] mb-4">FAQ</div>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight">
              Questions, calmly answered.
            </h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b border-[#1e1e1e]"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-left text-lg font-medium py-6 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#a0a0a0] leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="lazy-section relative py-[120px] px-6 sm:px-10 border-t border-[#181818] overflow-hidden">
        {/* Interactive 3D mark sits behind the CTA copy, dimmed so the
            headline and buttons remain the primary read. Lazy-loaded via
            IntersectionObserver inside <Logo3D />. */}
        <Logo3D background />
        {/* VHS/vintage CRT scanline + dust texture layered on top of the
            3D mark but under the text. Gives the whole background a
            slow-drifting analog-tape feel. */}
        <div className="vhs-texture" aria-hidden />
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <h2 className="text-5xl sm:text-6xl font-bold tracking-tighter leading-tight">
            Ready to grow faster?
          </h2>
          <p className="mt-6 text-lg text-[#a0a0a0] max-w-2xl mx-auto">
            Start your order in under 90 seconds. Human editors will handle the rest.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/order"
              data-testid="final-cta-order"
              className="btn-accent squircle px-8 py-4 font-medium btn-anim inline-flex items-center gap-2"
            >
              Order now <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="final-cta-contact"
              className="squircle border border-[#2b2b2b] px-8 py-4 font-medium hover:bg-[#111] btn-anim inline-flex items-center gap-2"
            >
              <Clock size={16} /> Enterprise inquiry
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
